import { COLORS } from "../assets/theme";
import { IAccount } from "../services/AccountService";
import { FieldType } from "./common/CustomDialog";
import AutoComplete from "./common/AutoComplete";

export interface IStepDialogData extends Omit<IAccount, "id"> {
  id?: IAccount["id"];
  windowSize?: number;
  port?: number;
  accountType: string;
  error?: string;
  ip: string;
  acceptedIps: string;
  // Flattened sendRequestField (VENDOR)
  sendReqSendApiKeyName?: string;
  sendReqSendApiKeyLocation?: string;
  sendReqUsernameName?: string;
  sendReqUsernameLocation?: string;
  sendReqPasswordName?: string;
  sendReqPasswordLocation?: string;
  sendReqDestinationName?: string;
  sendReqDestinationLocation?: string;
  sendReqSourceName?: string;
  sendReqSourceLocation?: string;
  sendReqTextName?: string;
  sendReqTextLocation?: string;
  sendReqDataCodingName?: string;
  sendReqDataCodingLocation?: string;
  sendReqHttpMethod?: string;
  sendReqContentType?: string;
  // Flattened sendResponseField (VENDOR)
  sendRespErrorCode?: string;
  sendRespErrorDescription?: string;
  sendRespMessageErrorCode?: string;
  sendRespMessageId?: string;
  sendRespOriginatingAddress?: string;
  sendRespDestinationAddress?: string;
  sendRespMessageCount?: string;
  sendRespMessageParts?: string;
  // Flattened dlrRequestField (CLIENT)
  dlrReqApiKeyName?: string;
  dlrReqApiKeyLocation?: string;
  dlrReqSenderName?: string;
  dlrReqSenderLocation?: string;
  dlrReqDestinationName?: string;
  dlrReqDestinationLocation?: string;
  dlrReqMessageIdName?: string;
  dlrReqMessageIdLocation?: string;
  dlrReqDeliveryStatusName?: string;
  dlrReqDeliveryStatusLocation?: string;
  dlrReqDateReceivedName?: string;
  dlrReqDateReceivedLocation?: string;
  dlrReqDescriptionName?: string;
  dlrReqDescriptionLocation?: string;
  dlrReqUsernameName?: string;
  dlrReqUsernameLocation?: string;
  dlrReqPasswordName?: string;
  dlrReqPasswordLocation?: string;
  dlrReqHttpMethod?: string;
  dlrReqContentType?: string;
  // Flattened dlrResponseField (CLIENT)
  dlrRespErrorCode?: string;
  dlrRespErrorDescription?: string;
}

const stripSuffix = (name: string): string =>
  ["-MO", "-MT", "-mo", "-mt"].some((s) => name.endsWith(s))
    ? name.slice(0, -3)
    : name;

const applySuffix = (name: string, dialogData: IStepDialogData): string => {
  const raw = stripSuffix(name);
  if (dialogData.type === "CLIENT") return raw + "-MO";
  if (dialogData.type === "VENDOR") return raw + "-MT";
  return raw;
};

// const passwordRules = [
//   {
//     label: "8 characters exactly",
//     test: (pw: string) => pw.length === 8,
//   },
//   {
//     label: "at least one uppercase",
//     test: (pw: string) => /[A-Z]/.test(pw),
//   },
//   {
//     label: "one lowercase",
//     test: (pw: string) => /[a-z]/.test(pw),
//   },
//   {
//     label: "one symbol",
//     test: (pw: string) => /[^a-zA-Z0-9]/.test(pw),
//   },
// ];

// function getFailedPasswordRules(pw: string) {
//   return passwordRules.filter((r) => !r.test(pw)).map((r) => r.label);
// }

// function getPasswordTooltip(pw: string) {
//   const failed = getFailedPasswordRules(pw);
//   return failed.length > 0 ? failed.join(" / ") : "";
// }

// Track if password field has been touched/modified by user
const hasPasswordBeenModified = new Map();
export const getHasHttp = (dialogData: IStepDialogData): boolean => {
  if (dialogData.type === "VENDOR") {
    return dialogData.connectionInterface === "HTTP";
  }
  if (dialogData.type === "CLIENT") {
    return (
      Array.isArray(dialogData.interfaces) &&
      (dialogData.interfaces as string[]).includes("HTTP")
    );
  }
  return false;
};

export const getStepLabels = (
  dialogMode: string,
  dialogData: IStepDialogData
): string[] => {
  if (dialogMode === "delete") return ["Delete Account"];
  const hasHttp = getHasHttp(dialogData);
  return hasHttp
    ? [
        "Account Details",
        "SMPP Details",
        "HTTP Details",
        "HTTP Request Fields",
        "HTTP Response Fields",
        "Additional Details",
      ]
    : ["Account Details", "SMPP Details", "Additional Details"];
};

const locationOptions = [
  { label: "URL", value: "URL" },
  { label: "BODY", value: "BODY" },
  { label: "HEADER", value: "HEADER" },
];

const httpMethodOptions = [
  { label: "GET", value: "GET" },
  { label: "POST", value: "POST" },
];

const contentTypeOptions = [
  { label: "application/json", value: "APPLICATION_JSON" },
  { label: "application/xml", value: "APPLICATION_XML" },
  { label: "text/plain", value: "TEXT_PLAIN" },
  { label: "application/x-www-form-urlencoded", value: "APPLICATION_FORM_URLENCODED" },
  { label: "multipart/form-data", value: "MULTIPART_FORM_DATA" },
];

const makeFieldPair = (
  label: string,
  nameValue: string,
  locationValue: string,
  onNameChange: (v: string) => void,
  onLocationChange: (v: string) => void,
  required = false,
  errors?: Record<string, boolean>,
  errorKey?: string
): FieldType[] => [
  {
    type: "text" as const,
    label: `${label} Field Name`,
    placeholder: `e.g., ${label.toLowerCase().replace(/ /g, "_")}`,
    value: nameValue,
    bgColor: COLORS.WhiteBackground,
    highlightLabelStar: required,
    error: required && errors && errorKey ? errors[errorKey] || false : false,
    onChange: (value: string | number) => onNameChange(String(value)),
  },
  {
    type: "select" as const,
    label: `${label} Location`,
    placeholder: "Select Location",
    options: locationOptions,
    value: locationValue || "URL",
    bgColor: COLORS.WhiteBackground,
    onChange: (value: string | number | (string | number)[]) =>
      onLocationChange(String(value)),
  },
];

export const getStepsData = (
  dialogData: IStepDialogData,
  dialogMode: string,
  errors: Record<string, boolean>,
  setDialogData: React.Dispatch<React.SetStateAction<IStepDialogData>>,
  setErrors: React.Dispatch<React.SetStateAction<Record<string, boolean>>>,
  routingPlanOptions: { label: string; value: string | number }[],
  profileOptions: { label: string; value: string | number }[],
  vpnTunnelOptions: { label: string; value: string | number }[],
  profileUsesVpn?: boolean,
  smppScheduleDeliveryTimeOptions: { label: string; value: string | number }[] = []
): FieldType[][] => {
  const hasHttp = getHasHttp(dialogData);

  const step0: FieldType[] = [
    {
      type: "text",
      label: "Account Name",
      placeholder: "Name",
      value: dialogData.name || "",
      error: errors.name || false,
      bgColor: COLORS.WhiteBackground,
      highlightLabelStar: true,
      helperText: errors.name ? "This field is required." : "",
      onChange: (value: string | number) => {
        const stringValue = value.toString();
        setDialogData((prev) => ({ ...prev, name: stringValue }));
        setErrors((prev) => ({ ...prev, name: !stringValue }));
      },
      onBlur: () => {
        setDialogData((prev) => ({
          ...prev,
          name: prev.type ? applySuffix(prev.name || "", prev) : prev.name,
        }));
      },
    },
    {
      type: "custom" as const,
      component:
        dialogData.type === "CLIENT" ? (
          <AutoComplete
            label="Account Mode"
            multiple={true}
            selectAll={true}
            hideChips={true}
            value={
              Array.isArray(dialogData.interfaces) ? dialogData.interfaces : []
            }
            options={[
              { label: "SMPP", value: "SMPP" },
              { label: "HTTP", value: "HTTP" },
            ]}
            onChange={(value: string | number | (string | number)[]) => {
              const arr = Array.isArray(value)
                ? value.map(String)
                : value
                ? [String(value)]
                : [];
              setDialogData((prev) => ({ ...prev, interfaces: arr }));
              setErrors((prev) => ({ ...prev, accountMode: arr.length === 0 }));
            }}
            clearable={true}
            placeholder="Account Mode"
            highlightLabelStar={true}
            error={errors.accountMode}
            helperText={errors.accountMode ? "This field is required." : ""}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: COLORS.WhiteBackground,
              },
            }}
          />
        ) : dialogData.type === "VENDOR" ? (
          <AutoComplete
            label="Account Mode"
            value={dialogData.connectionInterface ?? ""}
            options={[
              { label: "SMPP", value: "SMPP" },
              { label: "HTTP", value: "HTTP" },
            ]}
            onChange={(value: string | number | (string | number)[]) => {
              const v = String(value);
              setDialogData((prev) => ({
                ...prev,
                connectionInterface: v as "SMPP" | "HTTP",
              }));
              setErrors((prev) => ({ ...prev, accountMode: !v }));
            }}
            clearable={false}
            placeholder="Account Mode"
            highlightLabelStar={true}
            error={errors.accountMode}
            helperText={errors.accountMode ? "This field is required." : ""}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: COLORS.WhiteBackground,
              },
            }}
          />
        ) : (
          <AutoComplete
            label="Account Mode"
            value=""
            options={[]}
            onChange={() => {}}
            disabled={true}
            placeholder="Select Account Type first"
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: COLORS.WhiteBackground,
              },
            }}
          />
        ),
    },
    {
      type: "custom",
      component: (
        <AutoComplete
          label="Account Profile"
          value={dialogData.profileId ?? ""}
          options={profileOptions}
          onChange={(value: string | number | (string | number)[]) => {
            if (typeof value === "string" || typeof value === "number") {
              const stringValue = value.toString();
              setDialogData((prev) => ({ ...prev, profileId: stringValue }));
            }
          }}
          clearable={true}
          placeholder="Account Profile"
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: COLORS.WhiteBackground,
            },
          }}
        />
      ),
    },
    {
      type: "select",
      label: "Account Type",
      placeholder: "Select Account Type",
      options: [
        { label: "Customer", value: "CLIENT" },
        { label: "Provider", value: "VENDOR" },
      ],
      renderPlaceholder: true,
      value: dialogData.type ?? "",
      bgColor: COLORS.WhiteBackground,
      highlightLabelStar: true,
      error: errors.type || false,
      disabled: dialogMode === "edit",
      helperText: errors.type ? "This field is required." : "",
      onChange: (value: string | number | (string | number)[]) => {
        const stringValue = value.toString();
        setDialogData((prev) => ({
          ...prev,
          type: stringValue,
          name: prev.name
            ? applySuffix(prev.name, { ...prev, type: stringValue })
            : prev.name,
        }));
        setErrors((prev) => ({ ...prev, type: !stringValue }));
      },
    },

    {
      type: "select",
      label: "Account Status",
      placeholder: "Account Status",
      options: [
        { label: "Active", value: "true" },
        { label: "Inactive", value: "false" },
      ],
      value:
        dialogData.active !== undefined ? dialogData.active.toString() : "",
      bgColor: COLORS.WhiteBackground,
      highlightLabelStar: true,
      error: errors.active || false,
      helperText: errors.active ? "This field is required." : "",
      onChange: (value: string | number | (string | number)[]) => {
        const booleanValue = value === "true";
        setDialogData((prev) => ({ ...prev, active: booleanValue }));
        setErrors((prev) => ({ ...prev, active: value === "" }));
      },
    },
    dialogData.type === "CLIENT"
      ? {
          type: "custom" as const,
          component: (
            <AutoComplete
              label="Route Plan"
              value={dialogData.routingPlanId ?? ""}
              options={routingPlanOptions}
              onChange={(value: string | number | (string | number)[]) => {
                const stringValue =
                  typeof value === "string" ? value : value.toString();
                setDialogData((prev) => ({ ...prev, routingPlanId: stringValue }));
              }}
              clearable={true}
              placeholder="Route Plan"
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: COLORS.WhiteBackground,
                },
              }}
            />
          ),
        }
      : {
          type: "text" as const,
          hidden: true,
          value: "",
          onChange: (value: any) => {},
        },
  ];

  const step1: FieldType[] =
    dialogData.type === "CLIENT"
      ? [
          {
            type: "text",
            label: "Username",
            placeholder: "Username",
            value: dialogData.systemId || "",
            error: errors.systemId || false,
            bgColor: COLORS.WhiteBackground,
            highlightLabelStar: true,
            helperText: errors.systemId ? "This field is required." : "",
            onChange: (value: string | number) => {
              const stringValue =
                typeof value === "string" ? value : value.toString();
              setDialogData((prev) => ({ ...prev, systemId: stringValue }));
              setErrors((prev) => ({ ...prev, systemId: !stringValue }));
            },
          },
          {
            type: "text",
            label: "Window Size",
            placeholder: "Window Size",
            highlightLabelStar: true,
            value: dialogData.windowSize ?? "",
            bgColor: COLORS.WhiteBackground,
            onChange: (value: string | number) => {
              const numericValue = value === "" ? undefined : Number(value);

              setDialogData((prev) => ({ ...prev, windowSize: numericValue }));
            },
          },

          {
            type: "text",
            label: "Password",
            placeholder: "Password",
            value: dialogData.password || "",
            error:
            hasPasswordBeenModified.get(dialogData.id) && 
            !!errors.password,
            // (!!errors.password || !!getPasswordTooltip(dialogData.password || "")),
            bgColor: COLORS.WhiteBackground,
            highlightLabelStar: true,
          // tooltip: getPasswordTooltip(dialogData.password || ""),
            helperText: hasPasswordBeenModified.get(dialogData.id)
              ? errors.password
                ? "Password is required"
                : ""
              // : getPasswordTooltip(dialogData.password || "")
              : "",
            onChange: (value: string | number) => {
              hasPasswordBeenModified.set(dialogData.id, true);
              setDialogData((prev) => ({ ...prev, password: String(value) }));
              setErrors((prev) => ({ ...prev, password: !String(value) }));
            },
          },
          {
            type: "text",
            label: "Number of Binds",
            placeholder: "1",
            value: dialogData.maximumConnections ?? "",
            bgColor: COLORS.WhiteBackground,
            highlightLabelStar: false,
            typeinput: "number",
            onChange: (value) => {
              const numericValue = value === "" ? undefined : Number(value);
              setDialogData((prev) => ({
                ...prev,
                maximumConnections: numericValue,
              }));
            },
          },
          {
            type: "text",
            label: "IP",
            placeholder: "IP",
            value: dialogData.acceptedIps,
            bgColor: COLORS.WhiteBackground,
            tooltip: "Accepts multiple Ip addresses separated by commas",
            onChange: (value: string | number) => {
              const stringValue =
                typeof value === "string" ? value : value.toString();
              setDialogData((prev) => ({ ...prev, acceptedIps: stringValue }));
            },
          },
          {
            type: "text",
            typeinput: "number",
            label: "TPS",
            bgColor: COLORS.WhiteBackground,
            highlightLabelStar: true,
            value: dialogData.tps ?? "",
            onChange: (value) => {
              const numericValue = value === "" ? undefined : Number(value);
            setDialogData((prev) => ({
              ...prev,
              tps: numericValue,
            }));
            },
            placeholder: "Tps",
            error: !!errors.tps,
            helperText: errors.tps ? "tps is required" : "",
          },

          {
            type: "text",
            label: "Service Type",
            placeholder: "Service Type",
            value: dialogData.systemType || "",
            bgColor: COLORS.WhiteBackground,
            highlightLabelStar: false,
            onChange: (value: string | number) => {
              const stringValue =
                typeof value === "string" ? value.toString() : " ";
              setDialogData((prev) => ({ ...prev, systemType: stringValue }));
            },
          },

          {
            type: "text",
            typeinput: "number",
            label: "Hard TPS",
            bgColor: COLORS.WhiteBackground,
            highlightLabelStar: true,
            value: dialogData.tpsHardLimit ?? "",
            onChange: (value) => {
              const numericValue = value === "" ? undefined : Number(value);
            setDialogData((prev) => ({
              ...prev,
              tpsHardLimit: numericValue,
            }));
            },
            placeholder: "Hard Tps",
            error: !!errors.tpsHardLimit,
          helperText: errors.tptpsHardLimits ? "tps is required" : "",
          },

          {
            type: "select",
            label: "Connection Mode",
            placeholder: "Select Connection Mode",
            value: dialogData.bindType || "BIND_TX",
            bgColor: COLORS.WhiteBackground,
            highlightLabelStar: false,
            options: [
              { label: "BIND_TX", value: "BIND_TX" },
              { label: "BIND_RX", value: "BIND_RX" },
              { label: "BIND_TRX", value: "BIND_TRX" },
            ],
            onChange: (value: string | number | (string | number)[]) => {
              if (typeof value === "string" || typeof value === "number") {
                setDialogData((prev) => ({
                  ...prev,
                  bindType: value.toString(),
                }));
              }
            },
          },

          {
            type: "select",
            label: "Source NPI",
            placeholder: "Select Source NPI",
            value: dialogData.sourceNpi ?? "",
            bgColor: COLORS.WhiteBackground,
            options: [
              { label: "UNKNOWN", value: "UNKNOWN" },
              { label: "ISDN", value: "ISDN" },
              { label: "DATA", value: "DATA" },
              { label: "TELEX", value: "TELEX" },
              { label: "LAND_MOBILE", value: "LAND_MOBILE" },
              { label: "NATIONAL", value: "NATIONAL" },
              { label: "PRIVATE", value: "PRIVATE" },
              { label: "ERMES", value: "ERMES" },
              { label: "INTERNET", value: "INTERNET" },
              { label: "WAP", value: "WAP" },
            ],
            onChange: (value: string | number | (string | number)[]) => {
              if (typeof value === "string" || typeof value === "number") {
                setDialogData((prev) => ({
                  ...prev,
                  sourceNpi: value.toString(),
                }));
              }
            },
          },

          {
            type: "select",
            label: "Source TON",
            placeholder: "Select Source TON",
            value: dialogData.sourceTon ?? "",
            bgColor: COLORS.WhiteBackground,
            options: [
              { label: "UNKNOWN", value: "UNKNOWN" },
              { label: "INTERNATIONAL", value: "INTERNATIONAL" },
              { label: "NATIONAL", value: "NATIONAL" },
              { label: "NETWORK_SPECIFIC", value: "NETWORK_SPECIFIC" },
              { label: "SUBSCRIBER_NUMBER", value: "SUBSCRIBER_NUMBER" },
              { label: "ALPHANUMERIC", value: "ALPHANUMERIC" },
              { label: "ABBREVIATED", value: "ABBREVIATED" },
            ],
            onChange: (value: string | number | (string | number)[]) => {
              if (typeof value === "string" || typeof value === "number") {
                setDialogData((prev) => ({
                  ...prev,
                  sourceTon: value.toString(),
                }));
              }
            },
          },
          {
            type: "select",
            label: "Destination TON",
            placeholder: "Select Destination TON",
            value: dialogData.destinationTon ?? "",
            bgColor: COLORS.WhiteBackground,
            options: [
              { label: "UNKNOWN", value: "UNKNOWN" },
              { label: "INTERNATIONAL", value: "INTERNATIONAL" },
              { label: "NATIONAL", value: "NATIONAL" },
              { label: "NETWORK_SPECIFIC", value: "NETWORK_SPECIFIC" },
              { label: "SUBSCRIBER_NUMBER", value: "SUBSCRIBER_NUMBER" },
              { label: "ALPHANUMERIC", value: "ALPHANUMERIC" },
              { label: "ABBREVIATED", value: "ABBREVIATED" },
            ],
            onChange: (value: string | number | (string | number)[]) => {
              if (typeof value === "string" || typeof value === "number") {
                setDialogData((prev) => ({
                  ...prev,
                  destinationTon: value.toString(),
                }));
              }
            },
          },
          {
            type: "select",
            label: "Destination NPI",
            placeholder: "Select Destination NPI",
            value: dialogData.destinationNpi ?? "",
            bgColor: COLORS.WhiteBackground,
            options: [
              { label: "UNKNOWN", value: "UNKNOWN" },
              { label: "ISDN", value: "ISDN" },
              { label: "DATA", value: "DATA" },
              { label: "TELEX", value: "TELEX" },
              { label: "LAND_MOBILE", value: "LAND_MOBILE" },
              { label: "NATIONAL", value: "NATIONAL" },
              { label: "PRIVATE", value: "PRIVATE" },
              { label: "ERMES", value: "ERMES" },
              { label: "INTERNET", value: "INTERNET" },
              { label: "WAP", value: "WAP" },
            ],
            onChange: (value: string | number | (string | number)[]) => {
              if (typeof value === "string" || typeof value === "number") {
                setDialogData((prev) => ({
                  ...prev,
                  destinationNpi: value.toString(),
                }));
              }
            },
          },

          {
            type: "text",
            typeinput: "number",
            label: "Enquire Link",
            bgColor: COLORS.WhiteBackground,
            highlightLabelStar: true,
            value: dialogData.enquireLink ?? "",
            onChange: (value) => {
              const numericValue = value === "" ? undefined : Number(value);
            setDialogData((prev) => ({
              ...prev,
              enquireLink: numericValue,
            }));
            },
            placeholder: "Enquire Link",
            error: !!errors.enquireLink,
            helperText: errors.enquireLink ? "Enquire Link is required" : "",
          },
          {
            type: "text" as const,
            hidden: true,
            value: "",
            onChange: (value: any) => {},
          },
        ]
      : [
          {
            type: "text",
            label: "Username",
            placeholder: "Username",
            value: dialogData.systemId || "",
            error: errors.systemId || false,
            bgColor: COLORS.WhiteBackground,
            highlightLabelStar: true,
            helperText: errors.systemId ? "This field is required." : "",
            onChange: (value: string | number) => {
              const stringValue =
                typeof value === "string" ? value : value.toString();
              setDialogData((prev) => ({ ...prev, systemId: stringValue }));
              setErrors((prev) => ({ ...prev, systemId: !stringValue }));
            },
          },
          {
            type: "text",
            label: "Window Size",
            placeholder: "Window Size",
            highlightLabelStar: true,
            value: dialogData.windowSize ?? "",
            bgColor: COLORS.WhiteBackground,
            onChange: (value: string | number) => {
              const numericValue = value === "" ? undefined : Number(value);

              setDialogData((prev) => ({ ...prev, windowSize: numericValue }));
            },
          },
          {
            type: "text",
            label: "Password",
            placeholder: "Password",
            value: dialogData.password || "",
            error:
            hasPasswordBeenModified.get(dialogData.id) && 
            !!errors.password,
            // (!!errors.password || !!getPasswordTooltip(dialogData.password || "")),
            bgColor: COLORS.WhiteBackground,
            highlightLabelStar: true,
          // tooltip: getPasswordTooltip(dialogData.password || ""),
            helperText: hasPasswordBeenModified.get(dialogData.id)
              ? errors.password
                ? "Password is required"
                : ""
              // : getPasswordTooltip(dialogData.password || "")
              : "",
            onChange: (value: string | number) => {
              hasPasswordBeenModified.set(dialogData.id, true);
              setDialogData((prev) => ({ ...prev, password: String(value) }));
              setErrors((prev) => ({ ...prev, password: !String(value) }));
            },
          },
          {
            type: "text",
            label: "Number of Binds",
            placeholder: "Number of Binds",
            value: dialogData.maximumConnections ?? "",
            bgColor: COLORS.WhiteBackground,
            highlightLabelStar: false,
            typeinput: "number",
            onChange: (value) => {
              const numericValue = value === "" ? undefined : Number(value);
              setDialogData((prev) => ({
                ...prev,
                maximumConnections: numericValue,
              }));
            },
          },
          {
            type: "custom" as const,
            component: (
              <AutoComplete
                label="VPN Tunnel"
                value={dialogData.vpnTunnelId ?? ""}
                options={vpnTunnelOptions}
                onChange={(value: string | number | (string | number)[]) => {
                  if (typeof value === "string" || typeof value === "number") {
                    const selectedValue = String(value);
                    setDialogData((prev) => ({
                      ...prev,
                      vpnTunnelId: selectedValue ? Number(selectedValue) : null,
                    }));
                    setErrors((prev) => ({
                      ...prev,
                      vpnTunnelId: profileUsesVpn ? !selectedValue : false,
                    }));
                  }
                }}
                clearable={!profileUsesVpn}
                placeholder={profileUsesVpn ? "Select VPN tunnel" : "Optional VPN tunnel"}
                highlightLabelStar={profileUsesVpn}
                error={profileUsesVpn ? errors.vpnTunnelId || false : false}
                helperText={profileUsesVpn && errors.vpnTunnelId ? "This field is required." : ""}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: COLORS.WhiteBackground,
                  },
                }}
              />
            ),
          },

          {
            type: "text",
            label: "IP",
            placeholder: "IP",
            value: dialogData.ip,
            bgColor: COLORS.WhiteBackground,
            onChange: (value: string | number) => {
              const stringValue =
                typeof value === "string" ? value : value.toString();
              setDialogData((prev) => ({ ...prev, ip: stringValue }));
            },
          },
          {
            type: "text",
            typeinput: "number",
            label: "TPS",
            bgColor: COLORS.WhiteBackground,
            highlightLabelStar: true,
            value: dialogData.tps ?? "",
            onChange: (value) => {
              const numericValue = value === "" ? undefined : Number(value);
            setDialogData((prev) => ({
              ...prev,
              tps: numericValue,
            }));
            },
            placeholder: "Tps",
            error: !!errors.tps,
            helperText: errors.tps ? "tps is required" : "",
          },
          {
            type: "text",
            typeinput: "number",
            label: "Port",
            bgColor: COLORS.WhiteBackground,
            highlightLabelStar: true,
            value: dialogData.port ?? "",
            onChange: (value) => {
              const numericValue = value === "" ? undefined : Number(value);
            setDialogData((prev) => ({
              ...prev,
              port: numericValue,
            }));
            },
            placeholder: "Port",
            error: !!errors.port,
          helperText: errors.port ? "port is required" : "",
          },

          {
            type: "text",
            label: "Service Type",
            placeholder: "Service Type",
            value: dialogData.systemType,
            bgColor: COLORS.WhiteBackground,
            highlightLabelStar: false,
            onChange: (value: string | number) => {
              setDialogData((prev) => ({ ...prev, systemType: String(value) }));
            },
          },
          {
            type: "select",
            label: "Connection Mode",
            placeholder: "Select Connection Mode",
            value: dialogData.bindType || "BIND_TX",
            bgColor: COLORS.WhiteBackground,
            highlightLabelStar: false,
            options: [
              { label: "BIND_TX", value: "BIND_TX" },
              { label: "BIND_RX", value: "BIND_RX" },
              { label: "BIND_TRX", value: "BIND_TRX" },
            ],
            onChange: (value: string | number | (string | number)[]) => {
              if (typeof value === "string" || typeof value === "number") {
                setDialogData((prev) => ({
                  ...prev,
                  bindType: value.toString(),
                }));
              }
            },
          },
          {
            type: "select",
            label: "Source NPI",
            placeholder: "Select Source NPI",
            value: dialogData.sourceNpi ?? "UNKNOWN",
            bgColor: COLORS.WhiteBackground,
            options: [
              { label: "UNKNOWN", value: "UNKNOWN" },
              { label: "ISDN", value: "ISDN" },
              { label: "DATA", value: "DATA" },
              { label: "TELEX", value: "TELEX" },
              { label: "LAND_MOBILE", value: "LAND_MOBILE" },
              { label: "NATIONAL", value: "NATIONAL" },
              { label: "PRIVATE", value: "PRIVATE" },
              { label: "ERMES", value: "ERMES" },
              { label: "INTERNET", value: "INTERNET" },
              { label: "WAP", value: "WAP" },
            ],
            onChange: (value: string | number | (string | number)[]) => {
              if (typeof value === "string" || typeof value === "number") {
                setDialogData((prev) => ({
                  ...prev,
                  sourceNpi: value.toString(),
                }));
              }
            },
          },
          {
            type: "select",
            label: "Source TON",
            placeholder: "Select Source TON",
            value: dialogData.sourceTon ?? "UNKNOWN",
            bgColor: COLORS.WhiteBackground,
            options: [
              { label: "UNKNOWN", value: "UNKNOWN" },
              { label: "INTERNATIONAL", value: "INTERNATIONAL" },
              { label: "NATIONAL", value: "NATIONAL" },
              { label: "NETWORK_SPECIFIC", value: "NETWORK_SPECIFIC" },
              { label: "SUBSCRIBER_NUMBER", value: "SUBSCRIBER_NUMBER" },
              { label: "ALPHANUMERIC", value: "ALPHANUMERIC" },
              { label: "ABBREVIATED", value: "ABBREVIATED" },
            ],
            onChange: (value: string | number | (string | number)[]) => {
              if (typeof value === "string" || typeof value === "number") {
                setDialogData((prev) => ({
                  ...prev,
                  sourceTon: value.toString(),
                }));
              }
            },
          },
          {
            type: "select",
            label: "Destination TON",
            placeholder: "Select Destination TON",
            value: dialogData.destinationTon ?? "UNKNOWN",
            bgColor: COLORS.WhiteBackground,
            options: [
              { label: "UNKNOWN", value: "UNKNOWN" },
              { label: "INTERNATIONAL", value: "INTERNATIONAL" },
              { label: "NATIONAL", value: "NATIONAL" },
              { label: "NETWORK_SPECIFIC", value: "NETWORK_SPECIFIC" },
              { label: "SUBSCRIBER_NUMBER", value: "SUBSCRIBER_NUMBER" },
              { label: "ALPHANUMERIC", value: "ALPHANUMERIC" },
              { label: "ABBREVIATED", value: "ABBREVIATED" },
            ],
            onChange: (value: string | number | (string | number)[]) => {
              if (typeof value === "string" || typeof value === "number") {
                setDialogData((prev) => ({
                  ...prev,
                  destinationTon: value.toString(),
                }));
              }
            },
          },
          {
            type: "select",
            label: "Destination NPI",
            placeholder: "Select Destination NPI",
            value: dialogData.destinationNpi ?? "UNKNOWN",
            bgColor: COLORS.WhiteBackground,
            options: [
              { label: "UNKNOWN", value: "UNKNOWN" },
              { label: "ISDN", value: "ISDN" },
              { label: "DATA", value: "DATA" },
              { label: "TELEX", value: "TELEX" },
              { label: "LAND_MOBILE", value: "LAND_MOBILE" },
              { label: "NATIONAL", value: "NATIONAL" },
              { label: "PRIVATE", value: "PRIVATE" },
              { label: "ERMES", value: "ERMES" },
              { label: "INTERNET", value: "INTERNET" },
              { label: "WAP", value: "WAP" },
            ],
            onChange: (value: string | number | (string | number)[]) => {
              if (typeof value === "string" || typeof value === "number") {
                setDialogData((prev) => ({
                  ...prev,
                  destinationNpi: value.toString(),
                }));
              }
            },
          },

          {
            type: "text",
            typeinput: "number",
            label: "Enquire Link",
            bgColor: COLORS.WhiteBackground,
            highlightLabelStar: true,
            value: dialogData.enquireLink ?? "",
            onChange: (value) => {
              const numericValue = value === "" ? undefined : Number(value);
            setDialogData((prev) => ({
              ...prev,
              enquireLink: numericValue,
            }));
            },
            placeholder: "Enquire Link",
            error: !!errors.enquireLink,
            helperText: errors.enquireLink ? "Enquire Link is required" : "",
          },
          {
            type: "custom" as const,
            component: (
              <AutoComplete
                label="SMPP Schedule Delivery Time"
                value={dialogData.smppScheduleDeliveryTime ?? ""}
                options={smppScheduleDeliveryTimeOptions}
                onChange={(value: string | number | (string | number)[]) => {
                  const selectedValue =
                    typeof value === "string" || typeof value === "number"
                      ? String(value)
                      : "";
                  setDialogData((prev) => ({
                    ...prev,
                    smppScheduleDeliveryTime: selectedValue || undefined,
                  }));
                }}
                clearable={true}
                placeholder="Optional"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: COLORS.WhiteBackground,
                  },
                }}
              />
            ),
          },
        ];

  // Step: Additional Details
  const stepAdditional: FieldType[] =
    dialogData.type === "VENDOR"
      ? [
          {
            type: "switch" as const,
            label: "Cost Per Delivery",
            value: dialogData.costPerDelivery ?? false,
            highlightLabelStar: true,
            onChange: (value: boolean) => {
              setDialogData((prev) => ({ ...prev, costPerDelivery: value }));
            },
          },
          {
            type: "text" as const,
            hidden: true,
            value: "",
            onChange: (value: any) => {},
          }
        ]
      : [
          {
            type: "switch" as const,
            label: "Rate Per Delivery",
            value: dialogData.ratePerDelivery ?? false,
            highlightLabelStar: true,
            onChange: (value: boolean) => {
              setDialogData((prev) => ({ ...prev, ratePerDelivery: value }));
            },
          },
          {
            type: "text" as const,
            hidden: true,
            value: "",
            onChange: (value: any) => {},
          }
        ];

  if (!hasHttp) {
    return [step0, step1, stepAdditional];
  }

  // Step 2: HTTP Details
  const step2: FieldType[] =
    dialogData.type === "VENDOR"
      ? [
          {
            type: "text" as const,
            label: "HTTP Username",
            placeholder: "HTTP Username",
            value: dialogData.httpUsername || "",
            bgColor: COLORS.WhiteBackground,
            onChange: (value: string | number) => {
              setDialogData((prev) => ({
                ...prev,
                httpUsername: String(value),
              }));
            },
          },
          {
            type: "text" as const,
            label: "HTTP Password",
            placeholder: "HTTP Password",
            value: dialogData.httpPassword || "",
            bgColor: COLORS.WhiteBackground,
            onChange: (value: string | number) => {
              setDialogData((prev) => ({
                ...prev,
                httpPassword: String(value),
              }));
            },
          },
          {
            type: "text" as const,
            label: "Send Base URL",
            placeholder: "https://example.com/api",
            value: dialogData.sendBaseUrl || "",
            bgColor: COLORS.WhiteBackground,
            onChange: (value: string | number) => {
              setDialogData((prev) => ({
                ...prev,
                sendBaseUrl: String(value),
              }));
            },
          },
          {
            type: "text" as const,
            label: "Send API Key",
            placeholder: "API Key value",
            value: dialogData.sendApiKey || "",
            bgColor: COLORS.WhiteBackground,
            onChange: (value: string | number) => {
              setDialogData((prev) => ({
                ...prev,
                sendApiKey: String(value),
              }));
            },
          },
          {
            type: "text" as const,
            hidden: true,
            value: "",
            onChange: () => {},
          },
          {
            type: "text" as const,
            hidden: true,
            value: "",
            onChange: () => {},
          },
        ]
      : [
          {
            type: "text" as const,
            label: "DLR Base URL",
            placeholder: "https://example.com/dlr",
            value: dialogData.dlrBaseUrl || "",
            bgColor: COLORS.WhiteBackground,
            onChange: (value: string | number) => {
              setDialogData((prev) => ({
                ...prev,
                dlrBaseUrl: String(value),
              }));
            },
          },
          {
            type: "text" as const,
            label: "DLR API Key",
            placeholder: "DLR API Key value",
            value: dialogData.dlrApiKey || "",
            bgColor: COLORS.WhiteBackground,
            onChange: (value: string | number) => {
              setDialogData((prev) => ({
                ...prev,
                dlrApiKey: String(value),
              }));
            },
          },
          {
            type: "text" as const,
            hidden: true,
            value: "",
            onChange: () => {},
          },
          {
            type: "text" as const,
            hidden: true,
            value: "",
            onChange: () => {},
          },
        ];

  // Step 3: HTTP Request Field Details
  const step3: FieldType[] =
    dialogData.type === "VENDOR"
      ? [
          ...makeFieldPair(
            "Send API Key",
            dialogData.sendReqSendApiKeyName || "",
            dialogData.sendReqSendApiKeyLocation || "URL",
            (v) => setDialogData((p) => ({ ...p, sendReqSendApiKeyName: v })),
            (v) =>
              setDialogData((p) => ({ ...p, sendReqSendApiKeyLocation: v }))
          ),
          ...makeFieldPair(
            "Username",
            dialogData.sendReqUsernameName || "",
            dialogData.sendReqUsernameLocation || "URL",
            (v) => setDialogData((p) => ({ ...p, sendReqUsernameName: v })),
            (v) => setDialogData((p) => ({ ...p, sendReqUsernameLocation: v }))
          ),
          ...makeFieldPair(
            "Password",
            dialogData.sendReqPasswordName || "",
            dialogData.sendReqPasswordLocation || "URL",
            (v) => setDialogData((p) => ({ ...p, sendReqPasswordName: v })),
            (v) => setDialogData((p) => ({ ...p, sendReqPasswordLocation: v }))
          ),
          ...makeFieldPair(
            "Destination",
            dialogData.sendReqDestinationName || "",
            dialogData.sendReqDestinationLocation || "URL",
            (v) => setDialogData((p) => ({ ...p, sendReqDestinationName: v })),
            (v) =>
              setDialogData((p) => ({ ...p, sendReqDestinationLocation: v }))
          ),
          ...makeFieldPair(
            "Source",
            dialogData.sendReqSourceName || "",
            dialogData.sendReqSourceLocation || "URL",
            (v) => setDialogData((p) => ({ ...p, sendReqSourceName: v })),
            (v) => setDialogData((p) => ({ ...p, sendReqSourceLocation: v }))
          ),
          ...makeFieldPair(
            "Text",
            dialogData.sendReqTextName || "",
            dialogData.sendReqTextLocation || "URL",
            (v) => setDialogData((p) => ({ ...p, sendReqTextName: v })),
            (v) => setDialogData((p) => ({ ...p, sendReqTextLocation: v }))
          ),
          ...makeFieldPair(
            "Data Coding",
            dialogData.sendReqDataCodingName || "",
            dialogData.sendReqDataCodingLocation || "URL",
            (v) => setDialogData((p) => ({ ...p, sendReqDataCodingName: v })),
            (v) =>
              setDialogData((p) => ({ ...p, sendReqDataCodingLocation: v }))
          ),
          {
            type: "select" as const,
            label: "HTTP Method",
            placeholder: "Select Method",
            options: httpMethodOptions,
            value: dialogData.sendReqHttpMethod || "GET",
            bgColor: COLORS.WhiteBackground,
            onChange: (value: string | number | (string | number)[]) => {
              setDialogData((p) => ({
                ...p,
                sendReqHttpMethod: String(value),
              }));
            },
          },
          {
            type: "select" as const,
            label: "Content Type",
            placeholder: "Select Content Type",
            options: contentTypeOptions,
            value: dialogData.sendReqContentType || "APPLICATION_JSON",
            bgColor: COLORS.WhiteBackground,
            onChange: (value: string | number | (string | number)[]) => {
              setDialogData((p) => ({
                ...p,
                sendReqContentType: String(value),
              }));
            },
          },
        ]
      : [
          ...makeFieldPair(
            "DLR API Key",
            dialogData.dlrReqApiKeyName || "",
            dialogData.dlrReqApiKeyLocation || "URL",
            (v) => setDialogData((p) => ({ ...p, dlrReqApiKeyName: v })),
            (v) => setDialogData((p) => ({ ...p, dlrReqApiKeyLocation: v }))
          ),
          ...makeFieldPair(
            "Sender",
            dialogData.dlrReqSenderName || "",
            dialogData.dlrReqSenderLocation || "URL",
            (v) => setDialogData((p) => ({ ...p, dlrReqSenderName: v })),
            (v) => setDialogData((p) => ({ ...p, dlrReqSenderLocation: v }))
          ),
          ...makeFieldPair(
            "Destination",
            dialogData.dlrReqDestinationName || "",
            dialogData.dlrReqDestinationLocation || "URL",
            (v) => setDialogData((p) => ({ ...p, dlrReqDestinationName: v })),
            (v) =>
              setDialogData((p) => ({ ...p, dlrReqDestinationLocation: v }))
          ),
          ...makeFieldPair(
            "Message ID",
            dialogData.dlrReqMessageIdName || "",
            dialogData.dlrReqMessageIdLocation || "URL",
            (v) => setDialogData((p) => ({ ...p, dlrReqMessageIdName: v })),
            (v) => setDialogData((p) => ({ ...p, dlrReqMessageIdLocation: v }))
          ),
          ...makeFieldPair(
            "Delivery Status",
            dialogData.dlrReqDeliveryStatusName || "",
            dialogData.dlrReqDeliveryStatusLocation || "URL",
            (v) =>
              setDialogData((p) => ({ ...p, dlrReqDeliveryStatusName: v })),
            (v) =>
              setDialogData((p) => ({
                ...p,
                dlrReqDeliveryStatusLocation: v,
              }))
          ),
          ...makeFieldPair(
            "Date Received",
            dialogData.dlrReqDateReceivedName || "",
            dialogData.dlrReqDateReceivedLocation || "URL",
            (v) =>
              setDialogData((p) => ({ ...p, dlrReqDateReceivedName: v })),
            (v) =>
              setDialogData((p) => ({ ...p, dlrReqDateReceivedLocation: v }))
          ),
          ...makeFieldPair(
            "Description",
            dialogData.dlrReqDescriptionName || "",
            dialogData.dlrReqDescriptionLocation || "URL",
            (v) =>
              setDialogData((p) => ({ ...p, dlrReqDescriptionName: v })),
            (v) =>
              setDialogData((p) => ({ ...p, dlrReqDescriptionLocation: v }))
          ),
          ...makeFieldPair(
            "Username",
            dialogData.dlrReqUsernameName || "",
            dialogData.dlrReqUsernameLocation || "URL",
            (v) => setDialogData((p) => ({ ...p, dlrReqUsernameName: v })),
            (v) => setDialogData((p) => ({ ...p, dlrReqUsernameLocation: v }))
          ),
          ...makeFieldPair(
            "Password",
            dialogData.dlrReqPasswordName || "",
            dialogData.dlrReqPasswordLocation || "URL",
            (v) => setDialogData((p) => ({ ...p, dlrReqPasswordName: v })),
            (v) => setDialogData((p) => ({ ...p, dlrReqPasswordLocation: v }))
          ),
          {
            type: "select" as const,
            label: "HTTP Method",
            placeholder: "Select Method",
            options: httpMethodOptions,
            value: dialogData.dlrReqHttpMethod || "GET",
            bgColor: COLORS.WhiteBackground,
            onChange: (value: string | number | (string | number)[]) => {
              setDialogData((p) => ({
                ...p,
                dlrReqHttpMethod: String(value),
              }));
            },
          },
          {
            type: "select" as const,
            label: "Content Type",
            placeholder: "Select Content Type",
            options: contentTypeOptions,
            value: dialogData.dlrReqContentType || "APPLICATION_JSON",
            bgColor: COLORS.WhiteBackground,
            onChange: (value: string | number | (string | number)[]) => {
              setDialogData((p) => ({
                ...p,
                dlrReqContentType: String(value),
              }));
            },
          },
        ];

  // Step 4: HTTP Response Field Details
  const step4: FieldType[] =
    dialogData.type === "VENDOR"
      ? [
          {
            type: "text" as const,
            label: "Error Code Field",
            placeholder: "e.g., error_code",
            value: dialogData.sendRespErrorCode || "",
            bgColor: COLORS.WhiteBackground,
            onChange: (v: string | number) =>
              setDialogData((p) => ({ ...p, sendRespErrorCode: String(v) })),
          },
          {
            type: "text" as const,
            label: "Error Description Field",
            placeholder: "e.g., error_description",
            value: dialogData.sendRespErrorDescription || "",
            bgColor: COLORS.WhiteBackground,
            onChange: (v: string | number) =>
              setDialogData((p) => ({
                ...p,
                sendRespErrorDescription: String(v),
              })),
          },
          {
            type: "text" as const,
            label: "Message Error Code Field",
            placeholder: "e.g., message_error_code",
            value: dialogData.sendRespMessageErrorCode || "",
            bgColor: COLORS.WhiteBackground,
            onChange: (v: string | number) =>
              setDialogData((p) => ({
                ...p,
                sendRespMessageErrorCode: String(v),
              })),
          },
          {
            type: "text" as const,
            label: "Message ID Field",
            placeholder: "e.g., message_id",
            value: dialogData.sendRespMessageId || "",
            bgColor: COLORS.WhiteBackground,
            onChange: (v: string | number) =>
              setDialogData((p) => ({
                ...p,
                sendRespMessageId: String(v),
              })),
          },
          {
            type: "text" as const,
            label: "Originating Address Field",
            placeholder: "e.g., originating_address",
            value: dialogData.sendRespOriginatingAddress || "",
            bgColor: COLORS.WhiteBackground,
            onChange: (v: string | number) =>
              setDialogData((p) => ({
                ...p,
                sendRespOriginatingAddress: String(v),
              })),
          },
          {
            type: "text" as const,
            label: "Destination Address Field",
            placeholder: "e.g., destination_address",
            value: dialogData.sendRespDestinationAddress || "",
            bgColor: COLORS.WhiteBackground,
            onChange: (v: string | number) =>
              setDialogData((p) => ({
                ...p,
                sendRespDestinationAddress: String(v),
              })),
          },
          {
            type: "text" as const,
            label: "Message Count Field",
            placeholder: "e.g., message_count",
            value: dialogData.sendRespMessageCount || "",
            bgColor: COLORS.WhiteBackground,
            onChange: (v: string | number) =>
              setDialogData((p) => ({
                ...p,
                sendRespMessageCount: String(v),
              })),
          },
          {
            type: "text" as const,
            label: "Message Parts Field",
            placeholder: "e.g., message_parts",
            value: dialogData.sendRespMessageParts || "",
            bgColor: COLORS.WhiteBackground,
            onChange: (v: string | number) =>
              setDialogData((p) => ({
                ...p,
                sendRespMessageParts: String(v),
              })),
          },
        ]
      : [
          {
            type: "text" as const,
            label: "Error Code Field",
            placeholder: "e.g., error_code",
            value: dialogData.dlrRespErrorCode || "",
            bgColor: COLORS.WhiteBackground,
            onChange: (v: string | number) =>
              setDialogData((p) => ({ ...p, dlrRespErrorCode: String(v) })),
          },
          {
            type: "text" as const,
            label: "Error Description Field",
            placeholder: "e.g., error_description",
            value: dialogData.dlrRespErrorDescription || "",
            bgColor: COLORS.WhiteBackground,
            onChange: (v: string | number) =>
              setDialogData((p) => ({
                ...p,
                dlrRespErrorDescription: String(v),
              })),
          },
          {
            type: "text" as const,
            hidden: true,
            value: "",
            onChange: () => {},
          },
          {
            type: "text" as const,
            hidden: true,
            value: "",
            onChange: () => {},
          },
        ];

  return [step0, step1, step2, step3, step4, stepAdditional];
};
